import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Camera, Plus, RefreshCw, Calendar, FileText, CheckCircle2, ChevronRight, Archive, ArchiveRestore } from "lucide-react";
import {
  environmentSnapshotsListEnvironmentSnapshots,
  environmentSnapshotsCreateSnapshot,
  environmentSnapshotsArchiveEnvironmentSnapshot,
  environmentSnapshotsUnarchiveEnvironmentSnapshot,
} from "../../gen";
import type { SnapshotResponse, SnapshotListResponse } from "../../gen/models";
import { useOrg } from "../../hooks/useOrg";
import { useToast } from "../../context/ToastContext";
import { TimeAgo } from "../../components/common/TimeAgo";

export function SnapshotsWorkspace() {
  const { activeOrgId } = useOrg();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetSnapshotId = searchParams.get("snapshot_id");

  const [snapshots, setSnapshots] = useState<SnapshotResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSnapshot, setSelectedSnapshot] = useState<SnapshotResponse | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchSnapshots = async () => {
    setLoading(true);
    try {
      const resp = await environmentSnapshotsListEnvironmentSnapshots({
        include_archived: showArchived || Boolean(targetSnapshotId),
        limit: 100,
      });
      const data = (resp as any)?.data ?? resp;
      if (data && "snapshots" in data) {
        const listResp = data as SnapshotListResponse;
        setSnapshots(listResp.snapshots);
        if (listResp.snapshots.length > 0) {
          if (targetSnapshotId) {
            const matched = listResp.snapshots.find((s) => s.id === targetSnapshotId);
            if (matched) {
              setSelectedSnapshot(matched);
              return;
            }
          }
          if (!selectedSnapshot || !listResp.snapshots.some((s) => s.id === selectedSnapshot.id)) {
            const first = listResp.snapshots[0];
            setSelectedSnapshot(first);
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("snapshot_id", first.id);
                return next;
              },
              { replace: true },
            );
          } else {
            // Keep selected snapshot updated with newest payload
            const updated = listResp.snapshots.find((s) => s.id === selectedSnapshot.id);
            if (updated) setSelectedSnapshot(updated);
          }
        } else {
          setSelectedSnapshot(null);
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Unknown error", "Failed to load snapshots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSnapshots();
  }, [activeOrgId, showArchived, targetSnapshotId]);

  useEffect(() => {
    if (targetSnapshotId && snapshots.length > 0) {
      const matched = snapshots.find((s) => s.id === targetSnapshotId);
      if (matched && matched.id !== selectedSnapshot?.id) {
        setSelectedSnapshot(matched);
      }
    }
  }, [targetSnapshotId, snapshots]);

  const handleToggleArchive = async (snapshot: SnapshotResponse) => {
    setActionLoading(true);
    try {
      if (snapshot.is_archived) {
        await environmentSnapshotsUnarchiveEnvironmentSnapshot(snapshot.id);
        toast.success(`Snapshot '${snapshot.name}' restored.`, "Snapshot unarchived");
      } else {
        await environmentSnapshotsArchiveEnvironmentSnapshot(snapshot.id);
        toast.info(`Snapshot '${snapshot.name}' archived.`);
      }
      await fetchSnapshots();
    } catch (err: any) {
      toast.error(err.message || "Operation failed", "Error updating snapshot");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateSnapshot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setCreating(true);
    try {
      const resp = await environmentSnapshotsCreateSnapshot({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      const data = (resp as any)?.data ?? resp;
      if (data && "id" in data) {
        const created = data as SnapshotResponse;
        toast.success(`Environment snapshot '${created.name}' created successfully`, "Snapshot created");
        setIsModalOpen(false);
        setName("");
        setDescription("");
        await fetchSnapshots();
        setSelectedSnapshot(created);
      }
    } catch (err: any) {
      toast.error(err.message || "Could not freeze snapshot", "Creation failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex-1 flex h-full overflow-hidden bg-stone-50 dark:bg-stone-950">
      {/* Left List Column */}
      <div className="w-80 md:w-96 border-r border-stone-200 dark:border-stone-800 flex flex-col bg-white dark:bg-stone-900 shrink-0">
        <div className="p-4 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <h1 className="font-semibold text-stone-900 dark:text-stone-100">
              Baselines & Snapshots
            </h1>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={fetchSnapshots}
              className="p-1.5 text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200 rounded-md hover:bg-stone-100 dark:hover:bg-stone-800"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 bg-stone-900 hover:bg-stone-800 dark:bg-stone-100 dark:hover:bg-stone-200 text-white dark:text-stone-900 rounded-md transition"
            >
              <Plus className="w-3.5 h-3.5" />
              Freeze Baseline
            </button>
          </div>
        </div>

        {/* Filter Toggle */}
        <div className="px-4 py-2 bg-stone-50/70 dark:bg-stone-900/60 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
          <label className="text-xs text-stone-600 dark:text-stone-400 flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-stone-300 dark:border-stone-700 text-emerald-600 focus:ring-emerald-500"
            />
            Show archived snapshots
          </label>
          <span className="text-[11px] text-stone-400">
            {snapshots.length} total
          </span>
        </div>

        {/* List Content */}
        <div className="flex-1 overflow-y-auto divide-y divide-stone-100 dark:divide-stone-800/60">
          {loading && snapshots.length === 0 ? (
            <div className="p-8 text-center text-sm text-stone-500">Loading snapshots...</div>
          ) : snapshots.length === 0 ? (
            <div className="p-8 text-center text-sm text-stone-500">
              No snapshots frozen yet. Click "Freeze Baseline" to create your first baseline snapshot.
            </div>
          ) : (
            snapshots.map((snap) => {
              const isSelected = selectedSnapshot?.id === snap.id;
              return (
                <button
                  key={snap.id}
                  onClick={() => {
                    setSelectedSnapshot(snap);
                    setSearchParams(
                      (prev) => {
                        const next = new URLSearchParams(prev);
                        next.set("snapshot_id", snap.id);
                        return next;
                      },
                      { replace: false },
                    );
                  }}
                  className={`w-full text-left p-4 transition-colors flex items-start justify-between gap-3 ${
                    isSelected
                      ? "bg-emerald-50/60 dark:bg-emerald-950/20 border-l-2 border-emerald-600 dark:border-emerald-400"
                      : "hover:bg-stone-50 dark:hover:bg-stone-800/40"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-stone-900 dark:text-stone-100 truncate">
                        {snap.name}
                      </span>
                      {snap.is_archived && (
                        <span className="px-1.5 py-0.5 text-[10px] uppercase font-semibold tracking-wider bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400 rounded">
                          Archived
                        </span>
                      )}
                    </div>
                    {snap.description && (
                      <p className="text-xs text-stone-500 dark:text-stone-400 line-clamp-1 mt-0.5">
                        {snap.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-[11px] text-stone-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <TimeAgo date={snap.created_at} />
                      </span>
                      <span>•</span>
                      <span>
                        {(snap.rule_version_ids || []).length} rules, {(snap.knowledge_version_ids || []).length} items
                      </span>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 mt-1 text-stone-400 ${isSelected ? "text-emerald-600 dark:text-emerald-400" : ""}`} />
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Right Detail Pane */}
      <div className="flex-1 flex flex-col h-full overflow-y-auto">
        {selectedSnapshot ? (
          <div className="p-6 max-w-4xl space-y-6">
            <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">
                      {selectedSnapshot.name}
                    </h2>
                    {selectedSnapshot.is_archived && (
                      <span className="px-2 py-0.5 text-xs font-semibold uppercase tracking-wider bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400 rounded-md">
                        Archived
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-stone-500 mt-1">
                    {selectedSnapshot.description || "No description provided."}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleArchive(selectedSnapshot)}
                    disabled={actionLoading}
                    className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition ${
                      selectedSnapshot.is_archived
                        ? "border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800"
                        : "border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                    } disabled:opacity-50`}
                    title={selectedSnapshot.is_archived ? "Restore snapshot" : "Archive snapshot"}
                  >
                    {selectedSnapshot.is_archived ? (
                      <>
                        <ArchiveRestore className="w-3.5 h-3.5" />
                        Restore Snapshot
                      </>
                    ) : (
                      <>
                        <Archive className="w-3.5 h-3.5" />
                        Archive Snapshot
                      </>
                    )}
                  </button>
                  <div className="text-xs px-2.5 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/60 rounded-full flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Immutable Baseline
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-stone-100 dark:border-stone-800">
                <div className="p-4 bg-stone-50 dark:bg-stone-950 rounded-lg border border-stone-200 dark:border-stone-800">
                  <div className="text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Rule Versions
                  </div>
                  <div className="text-2xl font-bold text-stone-900 dark:text-stone-100 mt-1">
                    {(selectedSnapshot.rule_version_ids || []).length}
                  </div>
                </div>
                <div className="p-4 bg-stone-50 dark:bg-stone-950 rounded-lg border border-stone-200 dark:border-stone-800">
                  <div className="text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Knowledge Versions
                  </div>
                  <div className="text-2xl font-bold text-stone-900 dark:text-stone-100 mt-1">
                    {(selectedSnapshot.knowledge_version_ids || []).length}
                  </div>
                </div>
                <div className="p-4 bg-stone-50 dark:bg-stone-950 rounded-lg border border-stone-200 dark:border-stone-800">
                  <div className="text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Guardrail Versions
                  </div>
                  <div className="text-2xl font-bold text-stone-900 dark:text-stone-100 mt-1">
                    {(selectedSnapshot.guardrail_version_ids || []).length}
                  </div>
                </div>
              </div>
            </div>

            {/* Version IDs Manifest */}
            <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 space-y-4">
              <h3 className="font-semibold text-stone-900 dark:text-stone-100 flex items-center gap-2">
                <FileText className="w-4 h-4 text-stone-500" />
                Pinned Entity Versions
              </h3>
              <div className="space-y-3 text-xs">
                <div>
                  <span className="font-medium text-stone-700 dark:text-stone-300">Rules ({selectedSnapshot.rule_version_ids.length}):</span>
                  <div className="mt-1 p-2 bg-stone-50 dark:bg-stone-950 rounded font-mono text-[11px] text-stone-600 dark:text-stone-400 break-all max-h-28 overflow-y-auto">
                    {selectedSnapshot.rule_version_ids.length > 0 ? selectedSnapshot.rule_version_ids.join(", ") : "None"}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-stone-700 dark:text-stone-300">Knowledge Items ({selectedSnapshot.knowledge_version_ids.length}):</span>
                  <div className="mt-1 p-2 bg-stone-50 dark:bg-stone-950 rounded font-mono text-[11px] text-stone-600 dark:text-stone-400 break-all max-h-28 overflow-y-auto">
                    {selectedSnapshot.knowledge_version_ids.length > 0 ? selectedSnapshot.knowledge_version_ids.join(", ") : "None"}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-stone-700 dark:text-stone-300">Guardrails ({selectedSnapshot.guardrail_version_ids.length}):</span>
                  <div className="mt-1 p-2 bg-stone-50 dark:bg-stone-950 rounded font-mono text-[11px] text-stone-600 dark:text-stone-400 break-all max-h-28 overflow-y-auto">
                    {selectedSnapshot.guardrail_version_ids.length > 0 ? selectedSnapshot.guardrail_version_ids.join(", ") : "None"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-stone-400">
            Select a snapshot to view manifest details.
          </div>
        )}
      </div>

      {/* Freeze Snapshot Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-stone-900 rounded-xl max-w-md w-full p-6 shadow-xl border border-stone-200 dark:border-stone-800 space-y-4">
            <h3 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
              Freeze Environment Baseline
            </h3>
            <p className="text-xs text-stone-500">
              This captures and freezes the latest active versions of all Rules, Knowledge Items, and Guardrails in your organization into an immutable snapshot.
            </p>

            <form onSubmit={handleCreateSnapshot} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-stone-700 dark:text-stone-300 mb-1">
                  Snapshot Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Q4 Baseline - v1.0"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-700 dark:text-stone-300 mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  placeholder="Optional context about changes included in this baseline"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !name.trim()}
                  className="px-4 py-2 text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition disabled:opacity-50"
                >
                  {creating ? "Freezing..." : "Freeze Snapshot"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
